import {atom, useAtom, useAtomValue} from 'jotai';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useBlocker} from 'react-router-dom';
import {generateRandomUnsafeId} from 'webapp-lib/utils';

import {showConfirmationDialog} from '_/sharedComponents/ConfirmationDialog';

type FunctionType = (...args: any[]) => any;

type Config = {
  cleanup?: () => void;
  id: string;
  message: string;
  triggersOnNavigation?: boolean;
};

type Lock = {
  cleanup?: () => void;
  id: string;
  lockState: boolean;
  message: string;
  triggersOnNavigation?: boolean;
  reference: string;
};

type SafeCallbackOptions = {
  needsCleanup?: boolean;
  id?: string;
};

type SafeCallbackReturnType = {
  safeCallback: <T extends FunctionType | undefined>(
    arg0: T,
    options?: SafeCallbackOptions
  ) => T extends FunctionType
    ? (...args: Parameters<T>) => ReturnType<T> | undefined
    : undefined;
};

const globalLockState: Record<string, Lock> = {};

const isFunction = (object: any): object is FunctionType =>
  typeof object === 'function';

/**
 * Whether any registered lock (e.g. an open form dialog with unsaved edits)
 * is currently active - regardless of `triggersOnNavigation`, since a dirty
 * form dialog should count here even if it doesn't block in-app navigation.
 * Used to answer the Electron main process's UNSAVED_CHANGES_QUERY, see
 * useHandleUnsavedChangesQuery.ts.
 */
export function hasAnyActiveLock(): boolean {
  return Object.values(globalLockState).some(({lockState}) => lockState);
}

const getLockMessages = (locks: Lock[]) =>
  locks.length === 1
    ? locks[0]!.message
    : locks
        .filter(({lockState}) => lockState)
        .map(({message}) => `- ${message}`)
        .join('\n');

// used to trigger the global lock checking logic inside useLockBlocker
const lockBlockerTrigger = atom<boolean>(false);

export const useLockBlocker = (): void => {
  const {t} = useTranslation('common');

  // trigger re-render
  useAtomValue(lockBlockerTrigger);

  const locks = Object.values(globalLockState).filter(
    ({triggersOnNavigation, lockState}) => triggersOnNavigation && lockState
  );
  const hasActiveNavigationLocks = locks.length > 0;

  const blocker = useBlocker(hasActiveNavigationLocks);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (hasActiveNavigationLocks) {
        showConfirmationDialog({
          message: getLockMessages(locks),
          resolve: (ok) => (ok ? blocker.reset() : blocker.proceed()),
          invertOnCloseResolve: true,
          buttonProps: {
            cancelButtonText: t('buttons.yesDismissChange'),
            confirmationButtonText: t('buttons.no'),
          },
        });
      } else {
        blocker.proceed();
      }
    }
  }, [blocker, blocker.state, locks, t, hasActiveNavigationLocks]);
};

export const useLock = (
  {cleanup, id, message, triggersOnNavigation = true}: Config,
  isLocked: () => boolean,
  dependencies: readonly unknown[] = []
): SafeCallbackReturnType => {
  // reference number for this useLock
  const ref = useRef(generateRandomUnsafeId());
  const lockState = useMemo(
    (): boolean => isLocked(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...dependencies, isLocked]
  );
  // trigger re-render
  const [_, setLockBlockerTrigger] = useAtom(lockBlockerTrigger);

  useEffect(() => {
    if (
      globalLockState?.[id] &&
      globalLockState[id].reference !== ref.current
    ) {
      throw new Error(`Duplicate lock identifier: "${id}"`);
    }

    globalLockState[id] = {
      cleanup,
      id,
      lockState,
      message,
      triggersOnNavigation,
      reference: ref.current,
    };

    setLockBlockerTrigger((current) => !current);

    return () => {
      delete globalLockState[id];

      setLockBlockerTrigger((current) => !current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, message, lockState, triggersOnNavigation, setLockBlockerTrigger]);

  return useSafeCallback(id);
};

/**
 * Used for navigating from an edited form to not lose data. This method should be called before navigating.
 * @param idsToCheck which ids should be checked before navigating to another page.
 * Those ids should be used in the definition of the forms within the `useLock` hook
 */
export const useSafeCallback = <T extends FunctionType | undefined>(
  idsToCheck?: string | string[]
): SafeCallbackReturnType => {
  const {t} = useTranslation();
  const [_, setLockBlockerTrigger] = useAtom(lockBlockerTrigger);
  const concatIds = Array.isArray(idsToCheck)
    ? idsToCheck?.join(',')
    : idsToCheck;

  const ids = useMemo(
    () => (typeof idsToCheck === 'string' ? [idsToCheck] : idsToCheck),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [concatIds]
  );

  const getLockState = useCallback(() => {
    const activeFilters = Object.entries(globalLockState).filter(
      ([id]) => !ids || ids.includes(id)
    );
    if (activeFilters.length === 0) {
      return {locked: false};
    }
    return {
      locked: activeFilters.some(([_, lock]) => lock.lockState),
      message: getLockMessages(
        activeFilters
          .filter(([_, lock]) => lock.lockState)
          .map(([_, lock]) => lock)
      ),
      cleanup: (needsCleanup?: boolean) =>
        activeFilters.forEach(([_, lock]) =>
          needsCleanup ? lock.cleanup?.() : undefined
        ),
    };
  }, [ids]);

  // This "any" can be avoided when this issue is resolved: https://github.com/microsoft/TypeScript/issues/24929
  const safeCallback: any = useCallback(
    (
      callback: T,
      {needsCleanup = true}: SafeCallbackOptions = {
        needsCleanup: true,
      }
    ) => {
      if (!isFunction(callback)) {
        return undefined;
      }

      return async (
        ...args: Parameters<typeof callback>
      ): Promise<ReturnType<typeof callback> | undefined> => {
        const lockState = getLockState();

        if (
          !lockState.locked ||
          !(await new Promise((resolve) =>
            showConfirmationDialog({
              message: lockState.message,
              invertOnCloseResolve: true,
              resolve,
              buttonProps: {
                cancelButtonText: t('buttons.yesDismissChange'),
                confirmationButtonText: t('buttons.no'),
              },
            })
          ))
        ) {
          const newLockState = Object.entries(globalLockState).reduce(
            (state, [key, val]) => {
              // if no ids were given we unlock all locks
              let newLockedValue = !ids ? false : undefined;
              // if ids were given we only set the given ids to unlocked
              if (ids) {
                newLockedValue = ids.includes(key) ? false : val.lockState;
              }
              return {
                ...state,
                [key]: {...val, lockState: newLockedValue},
              };
            },
            {}
          );
          Object.assign(globalLockState, newLockState);
          setLockBlockerTrigger((current) => !current);
          lockState.cleanup?.(needsCleanup);
          return callback(...args);
        }

        return undefined;
      };
    },
    [ids, getLockState, t, setLockBlockerTrigger]
  );

  return {
    safeCallback,
  };
};
